// Web Audio API approach - more reliable for production audio
import { DRUMROLL_BASE64 } from './audioData';

let audioContext = null;
let cachedAudioBuffer = null;

const getAudioContext = () => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  return audioContext;
};

// Convert base64 string to ArrayBuffer
const base64ToArrayBuffer = (base64) => {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
};

export const playDrumrollWithWebAudio = async () => {
  try {
    const context = getAudioContext();
    
    // Resume context if suspended (required for user interaction)
    if (context.state === 'suspended') {
      await context.resume();
    }

    // Load or use cached audio buffer
    if (!cachedAudioBuffer) {
      console.log("🔊 Decoding embedded drumroll audio...");
      
      // Convert base64 to ArrayBuffer
      const arrayBuffer = base64ToArrayBuffer(DRUMROLL_BASE64);
      cachedAudioBuffer = await context.decodeAudioData(arrayBuffer);
      console.log("✓ Successfully decoded drumroll audio");
    }

    // Play the audio
    const source = context.createBufferSource();
    source.buffer = cachedAudioBuffer;
    source.connect(context.destination);
    source.start(0);
    console.log("✓ Playing drumroll using embedded audio data");

    return true;
  } catch (error) {
    console.error("✗ Embedded audio playback failed:", error.message);
    return false;
  }
};

// Fallback: Create synthesized drum roll using Web Audio API
const playFallbackDrumRoll = () => {
  try {
    const context = new (window.AudioContext || window.webkitAudioContext)();
    const now = context.currentTime;

    // Create 8 drum hits with decreasing frequency
    for (let i = 0; i < 8; i++) {
      const osc = context.createOscillator();
      const gain = context.createGain();
      
      osc.connect(gain);
      gain.connect(context.destination);
      
      // Decreasing pitch for drum effect
      osc.frequency.value = 200 - (i * 15);
      
      const startTime = now + (i * 0.05);
      const duration = 0.08;
      
      osc.start(startTime);
      osc.stop(startTime + duration);
      
      gain.gain.setValueAtTime(0.3, startTime);
      gain.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
    }
    
    console.log("▶ Playing fallback synthesized drum roll");
    return true;
  } catch (error) {
    console.error("✗ Fallback drum roll failed:", error.message);
    return false;
  }
};

// Primary function: Try Web Audio API with embedded audio, fall back to synthesized
export const playDrumrollAudio = async () => {
  console.log("🎵 Attempting to play drumroll...");
  
  // Try Web Audio API with embedded audio data
  const webAudioSuccess = await playDrumrollWithWebAudio();
  
  // If that fails, use synthesized fallback
  if (!webAudioSuccess) {
    console.log("🔄 Embedded audio failed, using synthesized fallback...");
    return playFallbackDrumRoll();
  }
  
  return webAudioSuccess;
};
